import {
  CreateProductRequest,
  GetProductsRequest,
  GetProductsResponse,
  Product,
  UpdateProductRequest,
  UsdaEligibilityResult,
} from '@/types/ProductType';
import { rtkApi } from '../rtkApi';

/**
 * Query args for screens that show every property (My Properties, header,
 * comparables). The API returns 20 by default, which silently hid the rest
 * of a buyer's properties.
 */
export const ALL_PRODUCTS = { limit: 1000 } as const;

export const productApi = rtkApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query<GetProductsResponse, GetProductsRequest>({
      query: (query) => ({ url: `/product`, params: query }),
      providesTags: ['Product'],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        // Full lists (ALL_PRODUCTS) get their own cache entry so a paged
        // list's first page can't replace them, or vice versa.
        const full = queryArgs?.limit === ALL_PRODUCTS.limit ? '-full' : '';
        return `${endpointName}-${queryArgs?.transactionMode ?? 'all'}-${
          queryArgs?.contractStarted ?? ''
        }${full}`;
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (currentCache?.products?.length && arg.offset) {
          return {
            products: [...currentCache.products, ...newResponse.products],
            isEnd: newResponse.isEnd,
          };
        }
        return newResponse;
      },
      forceRefetch({ currentArg, previousArg }) {
        return (
          currentArg?.offset !== previousArg?.offset ||
          currentArg?.contractStarted !== previousArg?.contractStarted ||
          currentArg?.transactionMode !== previousArg?.transactionMode
        );
      },
    }),
    getProduct: builder.query<Product, string>({
      query: (productId) => ({ url: `/product/${productId}` }),
      providesTags: (_response, _result, productId) => [
        { type: 'Product', id: productId },
      ],
    }),
    createProduct: builder.mutation<Product, CreateProductRequest>({
      query: (payload) => ({
        url: `/product`,
        method: 'POST',
        data: payload,
      }),
      invalidatesTags: ['Product'],
    }),
    updateProduct: builder.mutation<void, UpdateProductRequest>({
      query: (payload) => ({
        url: `/product/${payload.id}`,
        method: 'PUT',
        data: payload,
      }),
      invalidatesTags: (_response, _result, payload) => [
        { type: 'Product', id: payload.id },
      ],
    }),
    /** Customer action — looks the address up on USDA's eligibility map. */
    checkUsdaEligibility: builder.mutation<UsdaEligibilityResult, string>({
      query: (productId) => ({
        url: `/product/${productId}/usda-eligibility`,
        method: 'POST',
      }),
      invalidatesTags: (_response, _result, productId) => [
        { type: 'Product', id: productId },
        'Product',
      ],
    }),
    /** Owner or admin confirms or turns down a near-miss listing match. */
    resolveListingCandidate: builder.mutation<
      Product,
      { productId: string; decision: 'accept' | 'reject' }
    >({
      query: ({ productId, decision }) => ({
        url: `/product/${productId}/listing-candidate`,
        method: 'POST',
        data: { decision },
      }),
      invalidatesTags: (_response, _result, { productId }) => [
        { type: 'Product', id: productId },
        'Product',
      ],
    }),
    uploadMortgageStatement: builder.mutation<
      { success: boolean; product: Product },
      { productId: string; file: File }
    >({
      query: ({ productId, file }) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: `/product/${productId}/refinance/mortgage-statement`,
          method: 'POST',
          data: formData,
        };
      },
      invalidatesTags: (_response, _result, { productId }) => [
        { type: 'Product', id: productId },
        'Product',
      ],
    }),
    startContract: builder.mutation<void, string>({
      query: (productId) => ({
        url: `/product/${productId}/contract`,
        method: 'POST',
      }),
      invalidatesTags: (_response, _result, productId) => [
        { type: 'Product', id: productId },
        { type: 'Product' },
      ],
    }),
    removeProduct: builder.mutation<void, string>({
      query: (productId) => ({
        url: `/product/${productId}`,
        method: 'DELETE',
      }),
      async onQueryStarted(productId, { queryFulfilled, dispatch, getState }) {
        try {
          await queryFulfilled;
          const cache = productApi.util.selectInvalidatedBy(getState(), [
            { type: 'Product' },
          ]);
          cache
            .filter(({ endpointName }) => endpointName === 'getProducts')
            .forEach(({ originalArgs }) => {
              dispatch(
                productApi.util.updateQueryData(
                  'getProducts',
                  originalArgs,
                  (data) => {
                    data.products = data.products.filter(
                      (product: Product) => product._id !== productId
                    );
                    return data;
                  }
                )
              );
            });
        } catch (error) {
          console.error(error);
        }
      },
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useUploadMortgageStatementMutation,
  useCheckUsdaEligibilityMutation,
  useResolveListingCandidateMutation,
  useStartContractMutation,
  useRemoveProductMutation,
} = productApi;
